import * as React from 'react';

import cx from 'classnames';
import { debounce, isNil } from 'lodash';
import PropTypes from 'prop-types';

// @ts-ignore
import OrganizationActions from 'actions/OrganizationActions';
import CurrentUser from 'auth/CurrentUser';
// @ts-ignore
import OrganizationProfiles from 'collections/OrganizationProfiles';
// @ts-ignore
import Organizations from 'collections/Organizations';
import AsyncSelect from 'components/AsyncSelect';
import AsyncSelectItem from 'components/AsyncSelectItem';
import Link from 'components/Link';
// @ts-ignore
import SchoolColleaguesRow from 'components/SchoolColleaguesRow';
// @ts-ignore
import { NO_SCHOOL } from 'constants/OrganizationConstants';
// @ts-ignore
import Organization from 'entities/Organization';
import EventTracking from 'lib/EventTracking';
import { mountModal } from 'lib/modal';
// @ts-ignore
import { OrganizationCreateModal } from 'organizations/components/OrganizationCreate';
// @ts-ignore
import SettingsStore from 'stores/SettingsStore';
import errorLogger from 'utils/errorLogger';

import { DefaultProps as AsyncSelectProps } from './AsyncSelect';

import { Backbone$Id } from 'types/typescript/Backbone';

import './styles/SchoolPicker.scss';

const ORGANIZATIONS_PAGE_SIZE = 20;

const CREATE_SCHOOL_ITEM = -1;
const DEBOUNCE_DELAY = 300;

type Props = {
  onSelect: (itemKey: React.ReactText, title: string) => void;
  onClear: () => void;
  schoolId?: number;
  selectedSchool?: Organization;
  onCreateOrganization?: (org: Organization) => void;
  onNoSchool?: () => void;
  suggestions?: Organizations | Organization[];
  noQuery?: string;
  filterSearchResults?: Function;
  organizationType: 'School' | 'District';
  showNCESId?: boolean;
  className?: string;
} & Partial<AsyncSelectProps>;

type State = {
  defaultSchool: Organization | null;
  allPotentialSchoolsLoaded: boolean;
  loadedSchools: Organization[];
  query: string;
};

// TODO replace with OrganizationPicker
class SchoolPicker extends React.Component<Props, State> {
  static defaultProps = {
    organizationType: 'School',
  };

  static contextTypes = {
    eventScreenName: PropTypes.string,
  };

  constructor(props: Props) {
    super(props);
    this.state = this.constructInitialState();
  }

  componentWillMount() {
    if (this.props.selectedSchool) {
      this.setState({ defaultSchool: this.props.selectedSchool });
    }
  }

  componentDidMount() {
    this._mounted = true;
    if (this.props.schoolId && !this.state.defaultSchool) {
      OrganizationActions.getOrLoadOrganization(this.props.schoolId)
        .then((school: Organization) => {
          this.setState({ defaultSchool: school });
        })
        .catch(errorLogger('Organization load failure'));
    }
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  _mounted = false;

  constructInitialState = () => {
    const user = CurrentUser.getUser();
    // why do we need to test `user.worksAtASchool()` when `this.props.schoolId` says NO_SCHOOL?
    const noSchool =
      user && user.worksAtASchool() === false && this.props.schoolId === NO_SCHOOL.id;

    return {
      defaultSchool: noSchool ? NO_SCHOOL : null,
      allPotentialSchoolsLoaded: false,
      loadedSchools: [],
      query: '',
    };
  };

  onClear = () => {
    this.setState(this.constructInitialState(), this.props.onClear);
  };

  onCreateOrganization = (orgData: Organization | Object) => {
    EventTracking.tap(this.context.eventScreenName, 'SchoolPicker-createSchool', '', {
      query: this.state.query,
    });

    const { onCreateOrganization: onCreateOrganizationCb } = this.props;
    if (!onCreateOrganizationCb) {
      return;
    }

    // XXX because we have a Organization entity and a flow type
    const orgModel =
      orgData instanceof Organization ? orgData : (Organization.fromJSON(orgData) as Organization);
    OrganizationActions.addOrganization(orgModel);
    if (this._mounted) {
      this.setState({ defaultSchool: orgModel }, () => {
        onCreateOrganizationCb(orgModel);
      });
    } else {
      onCreateOrganizationCb(orgModel);
    }
  };

  onNoSchool = () => {
    EventTracking.tap(this.context.eventScreenName, 'dontWorkButton', '', {
      query: this.state.query,
    });
    this.setState({ defaultSchool: NO_SCHOOL }, () => {
      if (this.props.onNoSchool) {
        this.props.onNoSchool();
      }
    });
  };

  onSearchCallback = debounce((query: string, callback: Function) => {
    if (!query) {
      callback(new Promise(resolve => resolve([])));
      return;
    }
    EventTracking.tap(this.context.eventScreenName, 'SchoolPicker-search');
    // WTF while return?
    while (this.state.allPotentialSchoolsLoaded !== true) {
      return this.performSearch(query, callback); // eslint-disable-line consistent-return
    }
  }, DEBOUNCE_DELAY);

  onSelect = (item: React.ReactElement) => {
    if (!item.props) {
      return;
    }
    const key: string | number = item.props.id;
    this.setState({ defaultSchool: null });

    if (key === CREATE_SCHOOL_ITEM) {
      mountModal(this.getOrganizationCreateModal(), this);
    } else {
      if (key === NO_SCHOOL.id && this.props.onNoSchool) {
        this.onNoSchool();
      }
      this.props.onSelect(key, item.props.title);
    }
    EventTracking.tap(this.context.eventScreenName, 'SchoolPicker-select', {
      key,
    });
  };

  getOrganizationCreateModal = () => {
    return (
      <OrganizationCreateModal
        onCreate={this.onCreateOrganization}
        onRequestHide={() => undefined}
        // @ts-ignore ref implementation is weird.
        defaultOrganization={this.refs.schools.getSearchString()}
        organizationType={this.props.organizationType}
      />
    );
  };

  getSearchRequest = (query: string) => {
    const source = CurrentUser.isSignedIn() ? Organizations : OrganizationProfiles;

    const numLoaded = this.state.loadedSchools.length;
    const page = Math.floor(numLoaded / ORGANIZATIONS_PAGE_SIZE) + 1;

    return source.search(this.props.organizationType, query, page);
  };

  focus = () => {
    // @ts-ignore ref implementation is weird.
    this.refs.schools.focus();
  };

  performSearch = (query: string, callback: Function) => {
    callback(
      this.getSearchRequest(query).then((organizations: Organizations) => {
        const { filterSearchResults } = this.props;
        const filteredOrgs = filterSearchResults
          ? filterSearchResults(organizations.toArray())
          : organizations;

        const schools = this.state.loadedSchools;

        if (filteredOrgs.length < ORGANIZATIONS_PAGE_SIZE) {
          this.setState({ allPotentialSchoolsLoaded: true });
        }

        filteredOrgs.forEach(function(organization: Organization) {
          schools.push(organization);
          OrganizationActions.addOrganization(organization);
        });
        this.setState({ query, loadedSchools: schools });

        return schools.map(this.renderItemFromSchool);
      })
    );
  };

  initialItems = (): Array<React.ReactElement> | void => {
    const { suggestions } = this.props;

    if (isNil(suggestions)) {
      return;
    }

    if (suggestions instanceof Organizations) {
      return suggestions.map(this.renderItemFromSchool);
    } else {
      return suggestions.map(this.renderItemFromSchool);
    }
  };

  permanentItems = () => {
    const permanentItems = [];

    // If there's no callback to the create school
    // then we don't show the last permanent result.
    if (this.props.onCreateOrganization) {
      permanentItems.push(
        <AsyncSelectItem id={CREATE_SCHOOL_ITEM} title="">
          <div className="SchoolPicker-item SchoolPicker-item--perma SchoolPicker-new">
            Can’t find your {this.props.organizationType.toLowerCase()}?{' '}
            <Link weight="semibold">Add it now</Link>
          </div>
        </AsyncSelectItem>
      );
    }
    if (this.props.onNoSchool) {
      permanentItems.push(
        <AsyncSelectItem id={NO_SCHOOL.id} title={NO_SCHOOL.getName()}>
          <div className="SchoolPicker-item SchoolPicker-item--perma SchoolPicker-none">
            <Link weight="semibold">I don’t work at a school</Link>
          </div>
        </AsyncSelectItem>
      );
    }
    return permanentItems;
  };

  renderItemFromSchool = (organization: Organization) => {
    if (organization === NO_SCHOOL) {
      return this.renderNoSchool();
    }
    const address = organization.getFormattedAddress();
    const body = (
      <div className="SchoolPicker-item">
        <div className="SchoolPicker-item-title">{organization.getName()}</div>
        <div className="SchoolPicker-item-body">
          <address>{address}</address>
          <div>
            {this.props.showNCESId ? (
              organization.getNCESId() && 'NCES ID: ' + organization.getNCESId()
            ) : (
              <SchoolColleaguesRow school={organization} />
            )}
          </div>
        </div>
      </div>
    );
    return (
      <AsyncSelectItem id={organization.id} title={organization.getName()}>
        {body}
      </AsyncSelectItem>
    );
  };

  renderNoSchool = () => {
    const body = (
      <div className="SchoolPicker-item">
        <div className="SchoolPicker-item-title">{NO_SCHOOL.getName()}</div>
      </div>
    );
    return (
      <AsyncSelectItem id={NO_SCHOOL.id} title={NO_SCHOOL.getName()}>
        {body}
      </AsyncSelectItem>
    );
  };

  render() {
    const { className, ...others } = this.props;
    const minQuery = SettingsStore.getMinimumOrgQueryLength();
    const schoolItem = this.state.defaultSchool
      ? this.renderItemFromSchool(this.state.defaultSchool)
      : undefined;

    return (
      <div className={cx('SchoolPicker', className)}>
        <div className="SchoolPicker-AsyncWrapper">
          <AsyncSelect
            {...others}
            ref="schools"
            clearOnInputChange
            infiniteLoad
            inputDelay={500}
            defaultItem={schoolItem}
            initialItems={this.initialItems() || undefined}
            searchCallback={this.onSearchCallback}
            onSelect={this.onSelect}
            onClear={this.onClear}
            permanentItems={this.permanentItems()}
            queryMinSize={minQuery}
            noQuery={this.props.noQuery}
            queryTooShort={`Enter a minimum of ${
              minQuery === 1 ? '1 character' : `${minQuery || 0} characters`
            } to search for your school`}
          />
        </div>
      </div>
    );
  }
}

export default SchoolPicker;
